// Direction B "Ink Header" custom select. Replaces the native <select>
// for the Event Type field on the welcome form so the menu styling matches
// the rest of the kit:
//   - White surface, 1px border, 10px radius, soft shadow when open
//   - Rotating chevron, focus ring (3px brand-soft glow)
//   - Selected option: muted-50 bg, weight 500, brand-red check icon on the left
//   - Hover option: muted-100 bg
//   - Options use white-space: nowrap (don't wrap labels)
//   - Smart open direction: measures available space and flips upward when
//     there isn't enough room below. The Event Type field is the LAST in the
//     welcome form so it normally has to open up.
//   - Click-outside closes
//   - Keyboard: Enter/Space toggles, Esc closes, ArrowDown/Up navigate, Enter commits
//
// Why a hand-rolled dropdown instead of Radix / Headless UI: this widget bundles
// to ~169 KB gz with a 175 KB cap, and adds zero runtime deps. Pulling Radix
// (~30 KB gz minimum) or Headless UI (~12 KB) just for the welcome form's
// Event Type field would bust the cap; the design's interactions are simple
// enough that a self-contained component (~120 lines) covers it. If the chat
// surface grows more menu primitives in the future, that's the right time to
// bring in a library.
//
// Shadow DOM note: the widget renders inside a Shadow DOM root (see
// shadow-context.ts + bootloader.ts). Click events composed-path their way
// up to `document`, so the click-outside listener attached to `document` can
// inspect `event.composedPath()` to find the trigger / menu nodes inside the
// shadow tree.

import { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react'
import { CHEV_SVG, CHECK_SVG } from '../icons/icons'

export interface CustomSelectOption {
  value: string
  label: string
}

export interface CustomSelectProps {
  value: string
  onChange: (value: string) => void
  options: CustomSelectOption[]
  placeholder?: string
  // testid passed through to the trigger so tests can query it.
  testId?: string
  // Optional aria label override for the trigger button.
  ariaLabel?: string
}

// Visual: each option is 36px tall, padding 8px, plus 4px top/bottom on the
// menu. Used to estimate menuHeight before render so the smart-placement
// calculation doesn't have to wait for the actual DOM measurement.
const OPTION_HEIGHT_PX = 36
const MENU_VERTICAL_PADDING_PX = 8
const MENU_GAP_PX = 6
const MENU_MAX_HEIGHT_PX = 240

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  testId,
  ariaLabel,
}: CustomSelectProps) {
  const [open, setOpen] = useState(false)
  const [direction, setDirection] = useState<'down' | 'up'>('down')
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value) || null

  // ── Click-outside ────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      const path = e.composedPath()
      if (
        triggerRef.current && path.includes(triggerRef.current) ||
        menuRef.current && path.includes(menuRef.current)
      ) {
        return
      }
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointer, true)
    return () => document.removeEventListener('mousedown', onPointer, true)
  }, [open])

  // ── Smart placement: measure on open, flip up if not enough room below ─
  // Per design spec: "measures available space WITHIN the chat panel and
  // opens UPWARD when there isn't enough room below." We walk up to the
  // closest [data-testid="chat-panel"] ancestor and use its bounding rect
  // as the clipping window (NOT the browser viewport). When the trigger is
  // outside any panel — e.g. the dropdown is later reused on a marketing
  // surface — we fall back to viewport-based measurement.
  const recomputeDirection = useCallback(() => {
    if (!triggerRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const estimatedMenuHeight = Math.min(
      MENU_MAX_HEIGHT_PX,
      options.length * OPTION_HEIGHT_PX + MENU_VERTICAL_PADDING_PX,
    )
    const panel = triggerRef.current.closest('[data-testid="chat-panel"]') as HTMLElement | null
    let spaceBelow: number
    let spaceAbove: number
    if (panel) {
      const panelRect = panel.getBoundingClientRect()
      spaceBelow = panelRect.bottom - triggerRect.bottom
      spaceAbove = triggerRect.top - panelRect.top
    } else {
      spaceBelow = window.innerHeight - triggerRect.bottom
      spaceAbove = triggerRect.top
    }
    // Prefer "up" when above has more room AND the menu fits there. This
    // matches the spec's intent: the Event Type field is the last in the
    // welcome form, so its menu naturally opens upward toward the rest of
    // the form (instead of crowding the powered-by footer below). Falls
    // back to "down" otherwise.
    const fitsAbove = spaceAbove >= estimatedMenuHeight + MENU_GAP_PX
    if (fitsAbove && spaceAbove > spaceBelow) {
      setDirection('up')
    } else {
      setDirection('down')
    }
  }, [options.length])

  // ── Toggle + activeIndex setup ───────────────────────────────────────
  const openMenu = useCallback(() => {
    recomputeDirection()
    setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)))
    setOpen(true)
  }, [options, recomputeDirection, value])

  const closeMenu = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handleTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) openMenu()
      return
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      closeMenu()
    }
  }

  const handleMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      closeMenu()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % options.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + options.length) % options.length)
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < options.length) {
        onChange(options[activeIndex].value)
        closeMenu()
      }
    }
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKey}
        className={[
          'w-full h-10 px-3.5 pr-9 rounded-lg border bg-paper',
          'flex items-center justify-between gap-2',
          'font-sans text-sm text-left text-ink',
          'outline-none transition-colors',
          open ? 'border-ink ring-[3px] ring-accent/10' : 'border-border',
          'focus:border-ink focus:ring-[3px] focus:ring-accent/10',
        ].join(' ')}
      >
        <span className={selected ? 'text-ink truncate' : 'text-muted-400 truncate'}>
          {selected ? selected.label : placeholder}
        </span>
        <span
          dangerouslySetInnerHTML={{ __html: CHEV_SVG }}
          className={[
            'absolute right-3 top-1/2 -translate-y-1/2 text-muted-500 w-2.5 h-2.5',
            'transition-transform duration-150',
            open ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="listbox"
          tabIndex={-1}
          onKeyDown={handleMenuKey}
          className={[
            'absolute left-0 right-0 z-10',
            'bg-paper border border-border rounded-[10px]',
            'shadow-card py-1 outline-none',
            // Don't wrap labels inside options, per design spec.
            'whitespace-nowrap',
            // Smart placement: anchor below by default, flip above when there
            // isn't enough room. 6px gap between trigger + menu either way.
            direction === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          ].join(' ')}
          style={{ maxHeight: MENU_MAX_HEIGHT_PX, overflowY: 'auto' }}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === activeIndex
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-testid={`event-type-option-${opt.value}`}
                onMouseDown={(e) => {
                  // Prevent the click-outside mousedown handler from closing
                  // the menu before onClick fires (mousedown precedes click).
                  e.preventDefault()
                }}
                onClick={() => {
                  onChange(opt.value)
                  closeMenu()
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={[
                  'w-full text-left px-3 py-2 flex items-center gap-2',
                  'font-sans text-sm transition-colors',
                  isSelected ? 'bg-muted-50 font-medium text-ink' : 'text-ink',
                  isActive && !isSelected ? 'bg-muted-100' : '',
                  'hover:bg-muted-100',
                ].join(' ')}
              >
                <span
                  className={[
                    'w-3.5 h-3.5 inline-flex items-center justify-center flex-shrink-0',
                    isSelected ? 'text-accent' : 'text-transparent',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  <span dangerouslySetInnerHTML={{ __html: CHECK_SVG }} className="w-full h-full inline-block" />
                </span>
                <span className="flex-1">{opt.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
